import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users, X as XIcon, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

export function CcCard({ ticket, onChange }) {
  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const ccs = ticket.cc_emails || [];

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmed = email.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      const r = await api.post(`/tickets/${ticket.id}/cc`, { email: trimmed });
      onChange(r.data);
      setEmail("");
      setAdding(false);
      toast.success(`${trimmed} will be CC'd on all future replies`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add CC");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (addr) => {
    setBusy(true);
    try {
      const r = await api.delete(`/tickets/${ticket.id}/cc`, { params: { email: addr } });
      onChange(r.data);
      toast.success(`${addr} removed from CC list`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove CC");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hairline rounded-lg p-3 bg-white space-y-2" data-testid="cc-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[11px] uppercase text-slate-500 tracking-wider font-medium">
          <Users size={12} className="text-slate-500" />
          <span>Also on this thread</span>
        </div>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="text-[11px] text-[#4f4c90] hover:text-[#5F5CA5] flex items-center gap-0.5"
            data-testid="cc-add-open"
          >
            <Plus size={11} /> Add
          </button>
        )}
      </div>

      {ccs.length === 0 && !adding && (
        <div className="text-[11px] text-slate-400 font-mono">
          No one else is CC&apos;d. Replies go to {ticket.requester_email} only.
        </div>
      )}

      {ccs.length > 0 && (
        <div className="space-y-1">
          {ccs.map((e) => (
            <div key={e} className="flex items-center justify-between gap-2 text-xs bg-slate-50 rounded-md px-2 py-1.5 border border-slate-200">
              <span className="font-mono text-slate-700 truncate">{e}</span>
              <button
                type="button"
                onClick={() => remove(e)}
                disabled={busy}
                title="Remove from CC"
                className="text-slate-400 hover:text-red-500 shrink-0 disabled:opacity-40"
                data-testid={`cc-remove-${e}`}
              >
                <XIcon size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {adding && (
        <form onSubmit={submit} className="space-y-2 pt-1">
          <Input
            type="email"
            placeholder="name@company.com"
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
            autoFocus
            className="h-8 text-xs bg-white border-slate-200 font-mono"
            data-testid="cc-add-input"
          />
          <div className="flex items-center gap-1.5">
            <Button
              type="submit"
              disabled={busy || !email.trim()}
              className="h-7 text-[11px] bg-[#4f4c90] hover:bg-[#5F5CA5] text-white px-3"
              data-testid="cc-add-submit"
            >
              {busy ? "Adding…" : "Add"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => { setAdding(false); setEmail(""); }}
              className="h-7 text-[11px] text-slate-500 hover:text-slate-800 px-2"
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      {ccs.length > 0 && (
        <div className="text-[10px] text-slate-400 font-mono pt-1">
          Everyone above is CC&apos;d on every outbound reply.
        </div>
      )}
    </div>
  );
}
